import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Save } from 'lucide-react'

import { resolveApiErrorMessage, formatResolvedApiError } from '@/features/studio/lib/resolveApiError'
import { ProtoBtn, ProtoInput, ProtoLabel, ProtoModal } from '@/features/studio/components/ui-kit'

export function CanvasBoardModal({
  open,
  onClose,
  initialTitle,
  isEdit,
  onSubmit,
}: {
  open: boolean
  onClose: () => void
  initialTitle: string
  isEdit: boolean
  onSubmit: (title: string) => void | Promise<void>
}) {
  const { t } = useTranslation()
  const [title, setTitle] = useState(initialTitle)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setTitle(initialTitle)
    setError(null)
  }, [open, initialTitle])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = title.trim()
    if (!trimmed) return
    setBusy(true)
    setError(null)
    try {
      await onSubmit(trimmed)
    } catch (err) {
      setError(
        formatResolvedApiError(
          t,
          resolveApiErrorMessage(err, isEdit ? 'canvasBoards.renameFailed' : 'canvasBoards.createFailed'),
        ),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <ProtoModal
      open={open}
      onClose={onClose}
      title={t(isEdit ? 'canvasBoards.renameTitle' : 'canvasBoards.createTitle')}
      footer={
        <div className="flex justify-end gap-2">
          <ProtoBtn type="button" size="sm" variant="secondary" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </ProtoBtn>
          <ProtoBtn type="submit" form="canvas-board-form" size="sm" disabled={busy || !title.trim()}>
            <Save className="h-3.5 w-3.5" />
            {t('common.save')}
          </ProtoBtn>
        </div>
      }
    >
      <form id="canvas-board-form" onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        {error ? (
          <p className="rounded-xl border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</p>
        ) : null}
        <div>
          <ProtoLabel>{t('canvasBoards.fieldTitle')}</ProtoLabel>
          <ProtoInput
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('canvasBoards.fieldTitlePlaceholder')}
            autoFocus
          />
        </div>
      </form>
    </ProtoModal>
  )
}
